import { Injectable } from '@angular/core';
import { SafeHtml, DomSanitizer } from '@angular/platform-browser';
import {
    GlobalEmailTypes,
    AssessmentType,
    RoleType
} from '../constants-types/Enums';
import { DefaultEmailSettingDescription } from '../core-data-model/DefaultEmailSettingDescription';
import { CustomEmailSetting } from '../core-data-model/EmailDefault.model';
import { EmailDetails } from '../core-data-model/EmailDetails.model';
import { CompaniesDataService } from '../core-services/Companies.dataservice';
import { EmailDefaultApi } from '../core-services/EmailDefaultApi.connect';
import { notificationOnError } from '../core-services/Notifications.service';
import { StringUtilities } from '../core-utilities/String.utility';
import { FeatureFlagsService } from '../system-services/FeatureFlags.service';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';
import { TranslationService } from '../translations';

@Injectable({ providedIn: 'root' })
export class EmailDefaultService {
    behavioralAssessmentEmail: CustomEmailSetting = {
        subject: '',
        message: ''
    };
    cognitiveAssessmentEmail: CustomEmailSetting = {
        subject: '',
        message: ''
    };
    strategyAssessmentEmail: CustomEmailSetting = {
        subject: '',
        message: ''
    };

    constructor(
        public TR: TranslationService,
        public sanitizer: DomSanitizer,
        private emailDefaultApi: EmailDefaultApi,
        private stringUtil: StringUtilities,
        private initData: InitDataService,
        private userProfile: UserProfileSettingsService,
        private featureFlags: FeatureFlagsService,
        private company: CompaniesDataService
    ) {}

    @notificationOnError()
    getEmailDefaultTypes(
        languageLocale: string
    ): Promise<DefaultEmailSettingDescription[]> {
        return this.emailDefaultApi
            .getEmailDefaultTypes(languageLocale)
            .then((defaults) =>
                this.filterEmailDefaultsBasedOnProductAccess(defaults)
            );
    }

    getEmailDefaultDetails(
        emailType: GlobalEmailTypes,
        languageLocale: string
    ): Promise<CustomEmailSetting> {
        return this.emailDefaultApi
            .getEmailDefaultDetails(emailType, languageLocale)
            .then((emailDefault) => {
                if (emailType === GlobalEmailTypes.BehavioralAssessmentEmail) {
                    this.behavioralAssessmentEmail = emailDefault;
                } else if (
                    emailType === GlobalEmailTypes.CognitiveAssessmentEmail
                ) {
                    this.cognitiveAssessmentEmail = emailDefault;
                } else if (
                    emailType === GlobalEmailTypes.StrategyAssessmentEmail
                ) {
                    this.strategyAssessmentEmail = emailDefault;
                }
                return emailDefault;
            });
    }

    setEmailDefaultDetails(
        emailType: GlobalEmailTypes,
        emaildefault: CustomEmailSetting,
        languageLocale: string
    ): Promise<CustomEmailSetting> {
        return this.emailDefaultApi.setEmailDefaultDetails(
            emailType,
            emaildefault,
            languageLocale
        );
    }

    getInviteEmailContent(
        assessmentType: AssessmentType,
        emailDetails: EmailDetails,
        firstName: string
    ): SafeHtml {
        switch (assessmentType) {
            case AssessmentType.Behavioral:
                return this.getBAInviteEmailContent(emailDetails, firstName);
            case AssessmentType.Cognitive:
                return this.getCAInviteEmailContent(emailDetails);
            case AssessmentType.StrategyAssessment:
                return this.getSAInviteEmailContent(emailDetails, firstName);
            default:
                return null;
        }
    }

    private getBAInviteEmailContent(
        emailDetails: EmailDetails,
        firstName: string
    ): SafeHtml {
        // construct the pieces to then make the email via the new template used everywhere
        const emailTitle = this.TR.key(
            'BAInviteEmailTitle',
            emailDetails.language.locale
        );
        const emailGreeting = this.stringUtil.formatString(
            this.TR.key('BAInviteEmailGreeting', emailDetails.language.locale),
            firstName,
            emailDetails.administrator.fullName(),
            this.getCurrentCompanyName(emailDetails.administrator.company)
        );
        const emailLinkInfo = this.stringUtil.formatString(
            this.TR.key('BAInviteEmailLinkInfo', emailDetails.language.locale),
            this.initData.account.asssessmentSiteLoginUrl,
            emailDetails.assessmentExpirationDate.toLocaleDateString()
        );
        const emailNotes = this.TR.key(
            'BAInviteEmailNotes',
            emailDetails.language.locale
        );
        const emailSupportInfo = this.stringUtil.formatString(
            this.TR.key('BAInviteEmailNeedHelp', emailDetails.language.locale),
            emailDetails.administrator.fullName(),
            emailDetails.administrator.company,
            emailDetails.administrator.email
        );
        const globalEmailFooter = this.TR.key(
            'GlobalEmailFooter',
            emailDetails.language.locale
        );

        const inviteEmailContent = this.stringUtil.formatString(
            this.TR.key('BAInviteEmailContentTemplateV2'),
            emailGreeting,
            emailDetails.message,
            emailLinkInfo,
            emailNotes,
            emailSupportInfo
        );
        return this.sanitizer.bypassSecurityTrustHtml(
            this.stringUtil.formatString(
                this.TR.key(
                    'GlobalEmailTemplate',
                    emailDetails.language.locale
                ),
                emailTitle,
                inviteEmailContent,
                globalEmailFooter
            )
        );
    }

    private getCAInviteEmailContent(emailDetails: EmailDetails): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(
            this.stringUtil.formatString(
                this.TR.key(
                    'email.send.assessment.cognitive.message',
                    emailDetails.language.locale
                ),
                emailDetails.message,
                emailDetails.administrator.fullName(),
                emailDetails.administrator.company,
                '#',
                emailDetails.administrator.email,
                emailDetails.administrator.phone
            )
        );
    }

    private getSAInviteEmailContent(
        emailDetails: EmailDetails,
        firstName: string
    ): SafeHtml {
        // construct the pieces to then make the email via the new template used everywhere
        const emailTitle = this.TR.key(
            'email.send.assessment.strategy.invite.title',
            emailDetails.language.locale
        );
        const emailGreeting = this.stringUtil.formatString(
            this.TR.key(
                'email.send.assessment.strategy.invite.greeting',
                emailDetails.language.locale
            ),
            firstName,
            emailDetails.administrator.fullName(),
            emailDetails.administrator.company
        );
        const emailLinkInfo = this.stringUtil.formatString(
            this.TR.key('BAInviteEmailLinkInfo', emailDetails.language.locale),
            this.initData.account.asssessmentSiteLoginUrl,
            emailDetails.assessmentExpirationDate.toLocaleDateString()
        );

        const emailSupportInfo = this.stringUtil.formatString(
            this.TR.key('BAInviteEmailNeedHelp', emailDetails.language.locale),
            emailDetails.administrator.fullName(),
            emailDetails.administrator.company,
            emailDetails.administrator.email
        );
        const globalEmailFooter = this.TR.key(
            'GlobalEmailFooter',
            emailDetails.language.locale
        );

        const inviteEmailContent = this.stringUtil.formatString(
            this.TR.key(
                'email.send.assessment.strategy.invite.contentTemplate'
            ),
            emailGreeting,
            emailDetails.message,
            emailLinkInfo,
            '',
            '',
            emailSupportInfo
        );
        return this.sanitizer.bypassSecurityTrustHtml(
            this.stringUtil.formatString(
                this.TR.key(
                    'GlobalEmailTemplate',
                    emailDetails.language.locale
                ),
                emailTitle,
                inviteEmailContent,
                globalEmailFooter
            )
        );
    }

    private isDesignEnabled() {
        return (
            this.initData.user.hasPermissionLevelOrGreater(
                RoleType.AccountAdmin
            ) &&
            (this.initData.account.hasStrategy ||
                this.featureFlags.get('showStrategy'))
        );
    }

    filterEmailDefaultsBasedOnProductAccess(
        emailDefaults: DefaultEmailSettingDescription[]
    ) {
        if (!this.isDesignEnabled()) {
            emailDefaults = emailDefaults.filter(
                (emailDefault) =>
                    emailDefault.globalEmailTypeId !==
                    GlobalEmailTypes.StrategyAssessmentEmail
            );
        }
        if (!this.userProfile.isCognitiveEnabled()) {
            emailDefaults = emailDefaults.filter(
                (emailDefault) =>
                    emailDefault.globalEmailTypeId !==
                    GlobalEmailTypes.CognitiveAssessmentEmail
            );
        }

        return emailDefaults;
    }

    getCurrentCompanyName(defaultCompanyName: string) {
        return this.userProfile.currentCompany
            ? this.company.companies.find(
                  (company) => company.id === this.userProfile.currentCompany
              ).name
            : defaultCompanyName;
    }

    setSubject(
        type: GlobalEmailTypes,
        TR: TranslationService,
        languageLocale?: string
    ): string {
        const languageToUse = languageLocale || TR.currentLocale;
        let subject: string;
        switch (type) {
            case GlobalEmailTypes.BehavioralAssessmentEmail:
                subject =
                    this.behavioralAssessmentEmail.subject ||
                    TR.key(
                        'email.send.assessment.survey.pi.subject',
                        languageToUse
                    );
                break;
            case GlobalEmailTypes.CognitiveAssessmentEmail:
                subject =
                    this.cognitiveAssessmentEmail.subject ||
                    TR.key(
                        'email.send.assessment.cognitive.subject',
                        languageToUse
                    );
                break;
            case GlobalEmailTypes.JobAssessmentEmail:
                subject = TR.key(
                    'email.send.assessment.survey.pro.subject',
                    languageToUse
                );
                break;
            case GlobalEmailTypes.StrategyAssessmentEmail:
                subject =
                    this.strategyAssessmentEmail.subject ||
                    TR.key('GlobalEmailStrategySurveySubject', languageToUse);
                break;
        }
        return subject;
    }

    setMessage(
        type: GlobalEmailTypes,
        TR: TranslationService,
        languageLocale?: string
    ): string {
        const languageToUse = languageLocale || TR.currentLocale;
        let message: string;
        switch (type) {
            case GlobalEmailTypes.BehavioralAssessmentEmail:
                message =
                    this.behavioralAssessmentEmail.message ||
                    TR.key('GlobalEmailPiRemoteSurveyMessage', languageToUse);
                break;
            case GlobalEmailTypes.CognitiveAssessmentEmail:
                message =
                    this.cognitiveAssessmentEmail.message ||
                    TR.key(
                        'GlobalEmailCognitiveRemoteAssessmentMessage',
                        languageToUse
                    );
                break;
            case GlobalEmailTypes.JobAssessmentEmail:
                message = TR.key(
                    'GlobalEmailProRemoteSurveyMessage',
                    languageToUse
                );
                break;
            case GlobalEmailTypes.StrategyAssessmentEmail:
                message =
                    this.strategyAssessmentEmail.message ||
                    TR.key('GlobalEmailStrategySurveyMessage', languageToUse);
                break;
        }
        return message;
    }
}
